import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, User, Save, UserPlus, Info, Phone, FileText } from "lucide-react";
import { validateInternationalPhone } from "../../../utils/formValidators";
import Step1BasicInfo from "./Step1BasicInfo";
import Step2PersonalDetails from "./Step2PersonalDetails";
import Step3ContactDetails from "./Step3ContactDetails";
import Step4MotivationQuestions from "./Step4MotivationQuestions";

const emptyAttendee = () => ({
  firstName: "",
  lastName: "",
  nationality: "",
  email: "",
  gender: "",
  dateOfBirth: "",
  profession: "",
  organization: "",
  countryOfResidence: "",
  departureCity: "",
  idCardFront: "",
  contactNumber: "",
  address: "",
  motivationAnswers: [],
});

/** Convert form motivationAnswers (nested by section/question) to API motivation_answers array */
function motivationAnswersToApi(motivationalQuestions, motivationAnswers) {
  if (!motivationalQuestions?.length || !motivationAnswers) return [];
  const isNewFormat = motivationalQuestions[0]?.sectionTitle !== undefined || motivationalQuestions[0]?.section_title !== undefined;
  const out = [];
  if (isNewFormat) {
    motivationalQuestions.forEach((section, si) => {
      section.questions?.forEach((q, qi) => {
        const ans = motivationAnswers[si]?.[qi];
        if (ans !== undefined && ans !== null)
          out.push({
            section: section.sectionTitle ?? section.section_title ?? "",
            question: q.question,
            answer: Array.isArray(ans) ? ans.join(", ") : ans,
            type: q.type,
          });
      });
    });
  } else {
    motivationalQuestions.forEach((q, i) => {
      const ans = motivationAnswers[i];
      if (ans !== undefined && ans !== null)
        out.push({ question: q.question, answer: Array.isArray(ans) ? ans.join(", ") : ans });
    });
  }
  return out;
}

/** Convert API motivation_answers back to form motivationAnswers (nested) for editing */
function motivationAnswersFromApi(motivationalQuestions, motivation_answers) {
  if (!motivationalQuestions?.length || !Array.isArray(motivation_answers)) return [];
  const isNewFormat = motivationalQuestions[0]?.sectionTitle !== undefined || motivationalQuestions[0]?.section_title !== undefined;
  if (isNewFormat) {
    const form = [];
    motivationalQuestions.forEach((section, si) => {
      form[si] = [];
      section.questions?.forEach((q, qi) => {
        const item = motivation_answers.find((a) => a.question === q.question);
        form[si][qi] = item?.answer ?? "";
      });
    });
    return form;
  }
  return motivationalQuestions.map((q, i) => {
    const item = motivation_answers.find((a) => a.question === q.question);
    return item?.answer ?? "";
  });
}

const sanitizeAttendeeData = (data = {}) => {
  const { profilePhoto, ...rest } = data || {};
  return { ...emptyAttendee(), ...rest };
};

export default function AttendeeDrawer({
  open,
  onClose,
  attendeeIndex,
  initialData,
  onSave,
  onSaveAndAddNext,
  eventData,
  isLastSlot,
  totalSlots,
  motivationalQuestions,
}) {
  const [formData, setFormData] = useState(emptyAttendee());
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      if (initialData) {
        const base = sanitizeAttendeeData(initialData);
        const motivationAnswers =
          motivationalQuestions?.length && Array.isArray(initialData.motivation_answers)
            ? motivationAnswersFromApi(motivationalQuestions, initialData.motivation_answers)
            : (initialData.motivationAnswers ?? []);
        setFormData({ ...base, motivationAnswers });
      } else {
        setFormData(emptyAttendee());
      }
      setErrors({});
    }
  }, [open, attendeeIndex, initialData, motivationalQuestions]);

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName?.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName?.trim()) newErrors.lastName = "Last name is required";
    if (!formData.nationality?.trim()) newErrors.nationality = "Nationality is required";
    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.profession?.trim()) newErrors.profession = "Profession is required";
    if (!formData.organization?.trim()) newErrors.organization = "Organization is required";
    if (!formData.departureCity?.trim()) newErrors.departureCity = "Departure city is required";
    if (!formData.idCardFront) newErrors.idCardFront = "Front of ID card or passport is required";

    const contactValidation = validateInternationalPhone(formData.contactNumber);
    if (!contactValidation.isValid) {
      newErrors.contactNumber = contactValidation.message;
    }
    if (!formData.address?.trim()) newErrors.address = "Address is required";

    // Validate required motivation questions when present
    const mq = motivationalQuestions || [];
    if (mq.length > 0) {
      const isNewFormat = mq[0]?.sectionTitle !== undefined || mq[0]?.section_title !== undefined;
      const answers = formData.motivationAnswers ?? [];
      if (isNewFormat) {
        mq.forEach((section, si) => {
          section.questions?.forEach((q, qi) => {
            if (q.required) {
              const ans = answers[si]?.[qi];
              if (ans === undefined || ans === null || (typeof ans === "string" && !String(ans).trim())) {
                newErrors[`motivation_${si}_${qi}`] = "This question is required";
              }
            }
          });
        });
      } else {
        mq.forEach((q, i) => {
          if (q.required) {
            const ans = answers[i];
            if (ans === undefined || ans === null || (typeof ans === "string" && !String(ans).trim())) {
              newErrors[`motivation_${i}`] = "Required";
            }
          }
        });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = (addNext = false) => {
    if (!validate()) return;
    const data = sanitizeAttendeeData(formData);
    const motivation_answers =
      motivationalQuestions?.length > 0
        ? motivationAnswersToApi(motivationalQuestions, formData.motivationAnswers)
        : [];
    const payload = { ...data, motivation_answers };
    if (addNext && typeof onSaveAndAddNext === "function") {
      onSaveAndAddNext(payload);
    } else {
      onSave(payload);
      onClose();
    }
  };

  if (!open) return null;

  const handleIdCardUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) {
      setFormData((prev) => ({ ...prev, idCardFront: "" }));
      return;
    }
    if (!file.type.startsWith("image/")) {
      setErrors((prev) => ({ ...prev, idCardFront: "Please upload an image file" }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, idCardFront: "File size must be less than 5MB" }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFormData((prev) => ({ ...prev, idCardFront: reader.result }));
      setErrors((prev) => ({ ...prev, idCardFront: undefined }));
    };
    reader.readAsDataURL(file);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex justify-end"
      >
        <div
          className="absolute inset-0 bg-slate-900/45 backdrop-blur-sm"
          onClick={onClose}
          aria-hidden
        />
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "spring", damping: 28, stiffness: 300 }}
          className="relative flex h-[100dvh] w-full max-w-3xl flex-col bg-slate-50 shadow-2xl sm:w-[36rem] md:w-[42rem] lg:w-[48rem]"
        >
          <div className="sticky top-0 z-10 border-b border-gray-200 bg-white/95 px-4 py-4 backdrop-blur sm:px-6">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Attendee details</p>
                <h2 className="mt-1 text-lg font-semibold text-gray-900 sm:text-xl">
                  {initialData ? "Edit" : "Add"} attendee {attendeeIndex + 1} of {totalSlots}
                </h2>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-orange-100 px-2.5 py-1 text-xs font-medium text-orange-700">
                    Required fields marked *
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-gray-200 p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">
            <div className="mb-5 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
              <div className="flex items-start gap-2">
                <Info size={16} className="mt-0.5 shrink-0" />
                <p>
                  Fill in attendee details as shown on official documents to avoid registration issues.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <section className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
                <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                  <User size={14} className="text-teal-600" />
                  Basic information
                </h3>
                <Step1BasicInfo
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                />
              </section>

              <section className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
                <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Personal details
                </h3>
                <Step2PersonalDetails
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                  showProfilePhoto={false}
                />
                <div className="mt-4">
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Upload the front of your ID card (e.g., Identity Card or Passport) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleIdCardUpload}
                    className={`w-full rounded-lg border px-4 py-3 text-sm file:mr-4 file:rounded-lg file:border-0 file:bg-teal-50 file:px-4 file:py-2 file:text-teal-700 ${
                      errors?.idCardFront ? "border-red-300 bg-red-50" : "border-gray-300"
                    }`}
                  />
                  {formData.idCardFront && (
                    <div className="mt-3 flex flex-wrap items-start gap-3">
                      <div className="overflow-hidden rounded-lg border-2 border-teal-200 bg-white p-1">
                        <img
                          src={formData.idCardFront}
                          alt="ID card front preview"
                          className="h-24 w-40 object-cover rounded"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, idCardFront: "" }))}
                        className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-100"
                      >
                        Remove image
                      </button>
                    </div>
                  )}
                  {errors?.idCardFront && <p className="mt-1 text-sm text-red-500">{errors.idCardFront}</p>}
                </div>
              </section>

              <section className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
                <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                  <Phone size={14} className="text-teal-600" />
                  Contact details
                </h3>
                <Step3ContactDetails
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                  compact
                />
              </section>

              {motivationalQuestions?.length > 0 && (
                <section className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
                  <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                    <FileText size={14} className="text-teal-600" />
                    Additional Information
                  </h3>
                  <Step4MotivationQuestions
                    formData={formData}
                    setFormData={setFormData}
                    errors={errors}
                    motivationalQuestions={motivationalQuestions}
                    hideHeading
                  />
                </section>
              )}
            </div>
          </div>

          <div className="sticky bottom-0 border-t border-gray-200 bg-white px-4 py-4 sm:px-6">
            <div className="flex flex-col gap-2 sm:flex-row">
              <motion.button
                type="button"
                onClick={() => handleSave(false)}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-teal-600 px-4 py-3 text-sm font-semibold text-white shadow-md transition-colors hover:bg-teal-700"
              >
                <Save size={18} />
                Save attendee
              </motion.button>
              {!isLastSlot && onSaveAndAddNext && (
                <motion.button
                  type="button"
                  onClick={() => handleSave(true)}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl border-2 border-teal-600 bg-white px-4 py-3 text-sm font-semibold text-teal-600 transition-colors hover:bg-teal-50"
                >
                  <UserPlus size={18} />
                  Save & add next
                </motion.button>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 w-full text-center text-sm text-gray-500 transition-colors hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
